import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Home, User, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { requestPasswordReset } from '../services/passwordResetApi';
import { useToast } from '../components/Toast';
import { useI18n } from '../contexts/I18nContext';

const ForgotPassword: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setIsSubmitted(true);
      showSuccess(t('forgotPassword.success'));
    } catch (error: any) {
      console.error('Erro ao solicitar recuperação:', error);
      showError(error?.response?.data?.error || 'Erro ao processar solicitação.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #6b21a8, #7c3aed, #a855f7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '1rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '28rem',
    padding: '2rem',
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '2rem',
  };

  const titleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  };

  const inputGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.25rem',
  };

  const inputContainerStyle: React.CSSProperties = {
    position: 'relative',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    paddingLeft: '2.5rem',
    paddingRight: '1rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const iconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    height: '1.25rem',
    width: '1.25rem',
    color: '#9ca3af',
  };

  const submitButtonStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: '#9333ea',
    color: 'white',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  };

  const loadingStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const successStyle: React.CSSProperties = {
    padding: '1rem',
    backgroundColor: '#dcfce7',
    border: '1px solid #86efac',
    borderRadius: '0.5rem',
    color: '#166534',
    textAlign: 'center',
  };

  const linkStyle: React.CSSProperties = {
    color: '#9333ea',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    marginTop: '1rem',
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={titleStyle}>
            <Home style={{ height: '3rem', width: '3rem', color: '#9333ea', marginRight: '0.75rem' }} />
            <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>{t('login.appName')}</h1>
          </div>
          <p style={{ color: '#4b5563', margin: 0 }}>{t('login.tagline')}</p>
        </div>

        {/* Content */}
        {!isSubmitted ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>{t('forgotPassword.titleFull')}</h2>
              <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0' }}>{t('forgotPassword.subtitleFull')}</p>
            </div>

            <form onSubmit={handleSubmit} style={formStyle}>
              {/* Email */}
              <div style={inputGroupStyle}>
                <label htmlFor="forgot-password-email" style={labelStyle}>{t('common.email')}</label>
                <div style={inputContainerStyle}>
                  <Mail style={iconStyle} />
                  <input
                    id="forgot-password-email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={inputStyle}
                    placeholder={t('login.emailPlaceholder')}
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                style={submitButtonStyle}
                onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#7c3aed')}
                onMouseOut={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#9333ea')}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="spin" size={16} />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Mail size={16} />
                    Enviar Link de Recuperação
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          <div style={successStyle}>
            <Mail size={24} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: '600' }}>
              Email Enviado!
            </h3>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Se uma conta com esse email existir, você receberá um link para redefinir sua senha.
              Verifique sua caixa de entrada e spam.
            </p>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <Link to="/login" style={linkStyle}>
            <ArrowLeft size={16} />
            Voltar para o login
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        input:focus {
          outline: 2px solid transparent;
          outline-color: #a855f7;
          border-color: transparent;
        }
        button:focus {
          outline: 2px solid transparent;
          outline-offset: 2px;
        }
        button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;

